/**
 * Звук карточки сигнала.
 *
 * # Зачем он вообще
 *
 * Карточка живёт две минуты и появляется в тот момент, когда оператор смотрит на
 * другое окно серии, в другую вкладку или мимо экрана. Всё остальное в терминале
 * ждёт, пока на него посмотрят, а эта — нет: её единственный смысл в том, чтобы
 * решение было принято, пока цена кита ещё стоит.
 *
 * # Почему синтез, а не файл
 *
 * Ноль сетевых запросов и ноль вложений в сборку: два коротких тона собираются
 * осциллятором на месте. Файл пришлось бы тянуть — а звук нужен ровно тогда,
 * когда сеть уже показала себя ненадёжной (сигнал приехал сокетом на разрыве).
 *
 * # Почему браузер может промолчать
 *
 * Autoplay policy: AudioContext, созданный до первого жеста пользователя,
 * остаётся в состоянии suspended, и звука не будет. Лечится само — первым же
 * кликом по странице, в том числе по самому переключателю звука. Обещать
 * обратное нельзя, поэтому подсказка на переключателе говорит это вслух.
 */

/** Один контекст на вкладку: каждый новый — это ещё один аудиопоток в браузере. */
let ctx: AudioContext | null = null;

/** Громкость пика. Терминал смотрят часами, и сигнал не должен пугать. */
const PEAK = 0.12;

/** Два тона вверх: «внимание», а не «ошибка». Герцы, длительность — секунды. */
const NOTES: readonly { at: number; hz: number; ms: number }[] = [
  { at: 0, hz: 880, ms: 90 },
  { at: 0.1, hz: 1320, ms: 140 },
];

function audio(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor =
    typeof window === "undefined"
      ? undefined
      : (window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    // Браузер без Web Audio либо запрет политики — молчим. Карточка на экране
    // остаётся, и это по-прежнему главный канал.
    return null;
  }
  return ctx;
}

/**
 * Проиграть сигнал появления карточки.
 *
 * Никогда не бросает: отказ звука не имеет права уронить отрисовку карточки, из
 * которой тратят деньги.
 */
export function chime(): void {
  const ac = audio();
  if (!ac) return;
  try {
    // Контекст мог быть создан до первого жеста и потому спать. resume()
    // возвращает обещание, ждать которое нечего: если жеста ещё не было, звук
    // этого сигнала потерян, а следующий уже прозвучит.
    if (ac.state === "suspended") void ac.resume();
    const start = ac.currentTime;
    for (const note of NOTES) {
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      // Треугольник, а не синус: слышен на ноутбучном динамике при той же
      // громкости, и при этом не звенит, как пила.
      osc.type = "triangle";
      osc.frequency.value = note.hz;
      const from = start + note.at;
      const to = from + note.ms / 1000;
      // Огибающая обязательна: включённый и выключенный рывком осциллятор даёт
      // щелчок, который слышно громче самого тона.
      gain.gain.setValueAtTime(0.0001, from);
      gain.gain.exponentialRampToValueAtTime(PEAK, from + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, to);
      osc.connect(gain).connect(ac.destination);
      osc.start(from);
      osc.stop(to + 0.02);
    }
  } catch {
    // См. выше: звук — второстепенный канал.
  }
}
